import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useProfileRole } from '../../hooks/useProfileRole';
import { useJobStore } from '../../stores/jobStore';
import { LoadingSpinner } from '../LoadingSpinner';

interface RecruiterJobSearchRedirectProps {
  children: React.ReactNode;
}

/**
 * Route guard for `/job-search` (the freelancer job board). Recruiters have no business
 * there — the "Apply" action fails server-side for them (only freelancers can create
 * quotes) — so this redirects them away instead of letting the page render.
 *
 * `PrivateRoute` already guarantees a loaded profile before this renders, and already
 * redirects admins to `/admin`, so this component only needs to special-case recruiters.
 */
const RecruiterJobSearchRedirect: React.FC<RecruiterJobSearchRedirectProps> = ({ children }) => {
  const { isRecruiter } = useProfileRole();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('jobId') || undefined;

  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  useEffect(() => {
    if (!isRecruiter || !jobId) return;

    let cancelled = false;
    (async () => {
      try {
        // Status/visibility-agnostic ownership check (not the freelancer-facing job
        // search) — see the comment on jobStore's isJobOwner for why.
        const owns = await useJobStore.getState().isJobOwner(jobId);
        if (cancelled) return;

        if (owns) {
          // The recruiter's own job: send them to their project view instead of the
          // freelancer job board.
          setRedirectTo(`/projects/${jobId}`);
        } else {
          // Another recruiter's job, not found, or a fetch error: nothing useful to show.
          setRedirectTo('/jobs');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error checking job ownership for recruiter redirect:', error);
        setRedirectTo('/jobs');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isRecruiter, jobId]);

  if (!isRecruiter) {
    return <>{children}</>;
  }

  if (!jobId) {
    return <Navigate to="/jobs" replace />;
  }

  if (redirectTo) {
    return <Navigate to={redirectTo} replace />;
  }

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <LoadingSpinner />
    </div>
  );
};

export default RecruiterJobSearchRedirect;
